import React, { useRef, useState } from 'react';

import { Button, Checkbox, ChoiceTile, DropdownMenu, Input, ScrollBar, SparkIcon, Text } from '@spark';

import { createWorkspace, updateProfile } from '../api/workspacesApi';
import { PRIVACY_POLICY_URL, TERMS_URL } from '../content/legalLinks';
import onboardingTerms from '../content/onboardingTerms.txt?raw';
import { COUNTRIES, countryCodeForName, countryName } from '../utils/countries';
import { cropImageToSquare, logoImageErrorText } from '../utils/logoImage';
import useLogoCrop from '../hooks/useLogoCrop';
import LogoCropModal from './LogoCropModal';
import OnboardingLayout from './OnboardingLayout';
import WorkspaceDetailsFields from './WorkspaceDetailsFields';

/**
 * All wizard steps, in order (Figma "MVP-DESIGNES" 9224:4381, 9224:4435,
 * 9224:4431 / 9237:3395, 9226:4449, 9236:2369).  `completed_step` on the user
 * document counts finished steps 1..5 in *this* numbering, even when the
 * wizard shows a subset (see `steps` on `CreateWorkspaceWizard`), so a
 * resumed user is placed by `resolveInitialStep` regardless of which steps
 * were visible when they saved.
 */
export const ONBOARDING_STEPS = Object.freeze([
    { id: 'about', title: 'Tell us about yourself', artwork: 'about' },
    { id: 'workspace', title: 'Define your workspace', artwork: 'workspace' },
    { id: 'improve', title: 'What do you want to improve with Specific AI?', artwork: 'improve' },
    { id: 'audience', title: 'Who is the model for?', artwork: 'audience' },
    { id: 'terms', title: 'Review our terms', subtitle: 'Agree to the terms to continue', variant: 'wide' },
]);

export const CREATE_WORKSPACE_STEP_COUNT = ONBOARDING_STEPS.length;

/** 1-based position of a step id in `ONBOARDING_STEPS` — the value stored as `completed_step`. */
export const completedStepFor = (stepId) => ONBOARDING_STEPS.findIndex((step) => step.id === stepId) + 1;

/**
 * Steps that get a segment in the progress indicator (Figma 9251:5312 ..
 * 9237:3395): the split screens only — the terms screen has no indicator.
 */
export const progressSegmentsFor = (steps) => steps.filter((step) => step.id !== 'terms');

export const CONSENT_ITEMS = Object.freeze([
    { id: 'terms', label: 'I agree to the Terms & Conditions', linkLabel: 'Terms & Conditions', url: TERMS_URL },
    { id: 'privacy', label: 'I agree to the Privacy Policy', linkLabel: 'Privacy Policy', url: PRIVACY_POLICY_URL },
]);

/** Multi-select answers for "What do you want to improve with Specific AI?" (stored verbatim). */
export const IMPROVE_GOAL_OPTIONS = Object.freeze([
    'Reduce model costs',
    'Improve response speed',
    'Keep data private',
    'Improve task-specific accuracy',
    "Explore what's possible",
]);

/** Multi-select answers for "Who is the model for?" (stored verbatim). */
export const MODEL_AUDIENCE_OPTIONS = Object.freeze([
    'A customer-facing product',
    'An internal tool',
    'Research/experimentation',
    'A personal project',
]);

export const ONBOARDING_TERMS_TEXT = onboardingTerms;

const COUNTRY_ITEMS = COUNTRIES.map((country) => ({ label: country.name, searchText: country.name }));

const clampStep = (value, steps) => {
    const step = Number.isFinite(Number(value)) ? Math.trunc(Number(value)) : 0;
    return Math.min(Math.max(step, 0), steps.length - 1);
};

const toggleValue = (list, value) => (list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);

/**
 * Segmented step indicator at the bottom of the left column (Figma
 * 9251:5312): one 6px pill per split-screen step, filled up to and including
 * the current one.  Not rendered on the terms screen.
 */
function OnboardingProgress({ segments, position }) {
    if (!segments.length || position < 1) return null;
    return (
        <div
            className="onboarding-progress"
            role="progressbar"
            aria-label="Onboarding progress"
            aria-valuemin={1}
            aria-valuemax={segments.length}
            aria-valuenow={position}
            aria-valuetext={`Step ${position} of ${segments.length}`}
        >
            {segments.map((segment, index) => (
                <span
                    key={segment.id}
                    className={`onboarding-progress-segment${index < position ? ' onboarding-progress-segment-active' : ''}`}
                />
            ))}
        </div>
    );
}

/**
 * One consent row on the terms screen: a required Spark checkbox with the
 * exact Figma label, plus a "Read ..." link when the document URL is
 * published (`legalLinks.js`; empty today, so only the label renders).
 */
function ConsentCheckbox({ item, checked, disabled, onToggle }) {
    return (
        <div className="onboarding-consent-row">
            <Checkbox
                id={`onboarding-consent-${item.id}`}
                name={`consent_${item.id}`}
                label={item.label}
                status={checked ? 'active' : 'inactive'}
                disabled={disabled}
                required
                onChange={onToggle}
            />
            {item.url ? (
                <a className="onboarding-consent-link" href={item.url} target="_blank" rel="noopener noreferrer">
                    Read the {item.linkLabel}
                </a>
            ) : null}
        </div>
    );
}

/**
 * Creator / invitee onboarding (`/create-workspace`), rendered on
 * `OnboardingLayout`.  `steps` is the ordered subset of `ONBOARDING_STEPS` to
 * show (all five by default; `CreateWorkspacePage` drops "Define your
 * workspace" for an invitee whose workspace already exists).  Every "Next"
 * persists that step through `POST /users/set-profile` with `completed_step`
 * in the global 1..5 numbering, so the user resumes at the same point after a
 * reload or re-login:
 *
 * 1. Tell us about yourself — full name (required), company (prefilled from the
 *    email domain, kept for the workspace), country (required).
 * 2. Define your workspace — optional company logo, workspace name (required).
 *    `POST /workspaces` unless the user already owns one (`existingWorkspace`),
 *    in which case the name and logo are shown read-only.
 * 3. What do you want to improve with Specific AI? — multi-select, >= 1.
 * 4. Who is the model for? — multi-select, >= 1.
 * 5. Review our terms — both consent checkboxes are required; "Continue to
 *    Specific" is enabled once both are ticked, stamps `terms_accepted_at`
 *    (once) and hands the workspace to `onCreated` (the page reloads to `/`).
 */
export default function CreateWorkspaceWizard({
    steps = ONBOARDING_STEPS,
    initialName = '',
    initialCompany = '',
    initialCountry = '',
    initialStep = 0,
    initialGoals = [],
    initialAudience = [],
    initialTermsAccepted = false,
    existingWorkspace = null,
    onCreated,
    cropLogo = cropImageToSquare,
}) {
    const [step, setStep] = useState(() => clampStep(initialStep, steps));
    const [fullName, setFullName] = useState(initialName);
    const [company, setCompany] = useState(initialCompany);
    const [country, setCountry] = useState(countryName(initialCountry) ? String(initialCountry).toUpperCase() : '');
    const [workspaceName, setWorkspaceName] = useState(existingWorkspace?.name || '');
    const [logo, setLogo] = useState(existingWorkspace?.logo || null);
    const [goals, setGoals] = useState(() => initialGoals.filter((value) => IMPROVE_GOAL_OPTIONS.includes(value)));
    const [audience, setAudience] = useState(() =>
        initialAudience.filter((value) => MODEL_AUDIENCE_OPTIONS.includes(value)),
    );
    const [agreed, setAgreed] = useState(Boolean(initialTermsAccepted));
    const [consents, setConsents] = useState(() =>
        Object.fromEntries(CONSENT_ITEMS.map((item) => [item.id, Boolean(initialTermsAccepted)])),
    );
    const [createdWorkspace, setCreatedWorkspace] = useState(existingWorkspace);
    const [fieldErrors, setFieldErrors] = useState({});
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const termsScrollRef = useRef(null);

    const current = steps[step];
    const titleId = `onboarding-step-${current.id}-title`;
    const hasWorkspace = Boolean(createdWorkspace);
    const allConsented = CONSENT_ITEMS.every((item) => consents[item.id]);
    const progressSegments = progressSegmentsFor(steps);
    const progressPosition = progressSegments.findIndex((segment) => segment.id === current.id) + 1;

    const clearErrors = () => {
        if (Object.keys(fieldErrors).length) setFieldErrors({});
        if (error) setError(null);
    };

    const goBack = () => {
        clearErrors();
        setStep((prev) => Math.max(prev - 1, 0));
    };

    const advance = () => {
        clearErrors();
        setStep((prev) => Math.min(prev + 1, steps.length - 1));
    };

    const save = async (fields) => {
        const result = await updateProfile(fields);
        if (!result.ok) setError(result.error);
        return result.ok;
    };

    const submitAbout = async () => {
        const name = fullName.trim();
        const errors = {};
        if (!name) errors.fullName = 'Please enter your full name.';
        if (!country) errors.country = 'Please select your country.';
        if (Object.keys(errors).length) {
            setFieldErrors(errors);
            return;
        }
        clearErrors();
        setSubmitting(true);
        const ok = await save({ name, country, completed_step: completedStepFor('about') });
        setSubmitting(false);
        if (ok) advance();
    };

    const submitWorkspace = async () => {
        const name = workspaceName.trim();
        if (!name) {
            setFieldErrors({ workspaceName: 'Please enter a workspace name.' });
            return;
        }
        clearErrors();
        setSubmitting(true);
        if (!(await save({ completed_step: completedStepFor('workspace') }))) {
            setSubmitting(false);
            return;
        }
        if (!hasWorkspace) {
            const result = await createWorkspace({ name, company, logo });
            if (!result.ok) {
                setSubmitting(false);
                // A taken name is fixed in the field itself; anything else is a
                // step-level failure.
                if (result.nameTaken) setFieldErrors({ workspaceName: result.error });
                else setError(result.error);
                return;
            }
            setCreatedWorkspace(result.data?.workspace ?? { name });
        }
        setSubmitting(false);
        advance();
    };

    const submitChoices = async (values, key, label, completedStep) => {
        if (!values.length) {
            setFieldErrors({ [key]: label });
            return;
        }
        clearErrors();
        setSubmitting(true);
        const ok = await save({ [key]: values, completed_step: completedStep });
        setSubmitting(false);
        if (ok) advance();
    };

    const toggleConsent = (id) => {
        if (submitting) return;
        clearErrors();
        setConsents((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    /**
     * "Continue to Specific": record the agreement once (`terms_accepted_at`
     * is stamped server-side on `accept_terms`), then hand over.  A failed
     * save keeps the checkboxes ticked so the user can simply retry.
     */
    const finish = async () => {
        if (!allConsented || submitting) return;
        if (!agreed) {
            clearErrors();
            setSubmitting(true);
            const ok = await save({ accept_terms: true, completed_step: completedStepFor('terms') });
            setSubmitting(false);
            if (!ok) return;
            setAgreed(true);
        }
        onCreated?.(createdWorkspace ?? null);
    };

    // Picked file → crop dialog → `setLogo(dataUrl)`; a failure shows the
    // step-level error and keeps whatever logo was already chosen.
    const logoCrop = useLogoCrop({
        onLogo: (dataUrl) => {
            setError(null);
            setLogo(dataUrl);
        },
        onError: (err) => setError(logoImageErrorText(err)),
    });

    const nextHandlers = {
        about: submitAbout,
        workspace: submitWorkspace,
        improve: () =>
            submitChoices(goals, 'improve_goals', 'Please pick at least one option.', completedStepFor('improve')),
        audience: () =>
            submitChoices(audience, 'model_audience', 'Please pick at least one option.', completedStepFor('audience')),
    };

    // `undefined` (not '') lets the select fall back to its placeholder.
    const countryLabel = countryName(country) || undefined;

    const renderAbout = () => (
        <div className="onboarding-fields">
            <Input
                id="onboarding-full-name"
                size="large"
                label="Full Name"
                showAsterisk
                name="name"
                autoComplete="name"
                placeholder="Full Name"
                value={fullName}
                danger={Boolean(fieldErrors.fullName)}
                showHelperText={Boolean(fieldErrors.fullName)}
                helperText={fieldErrors.fullName || ''}
                disabled={submitting}
                onChange={(event) => {
                    setFullName(event.target.value);
                    clearErrors();
                }}
            />
            <Input
                id="onboarding-company"
                size="large"
                label="Company Name"
                name="organization"
                autoComplete="organization"
                placeholder="Your company"
                value={company}
                disabled={submitting}
                onChange={(event) => {
                    setCompany(event.target.value);
                    clearErrors();
                }}
            />
            <div className="onboarding-fields">
                <DropdownMenu
                    size="medium"
                    fill
                    selectType="single-select"
                    label="From"
                    showAsterisk
                    placeholder="Select Country"
                    showPrefixIcon
                    prefixIcon={<SparkIcon slug="globe" size="small" />}
                    searchable
                    searchMode="trigger"
                    searchPlaceholder="Search countries"
                    searchEmptyText="No matching country"
                    items={COUNTRY_ITEMS}
                    value={countryLabel}
                    disabled={submitting}
                    onSelect={(item) => {
                        setCountry(countryCodeForName(item?.label) || '');
                        clearErrors();
                    }}
                />
                {fieldErrors.country ? (
                    <Text as="p" variant="detail-regular" className="onboarding-error" role="alert">
                        {fieldErrors.country}
                    </Text>
                ) : null}
            </div>
        </div>
    );

    const renderWorkspace = () => (
        <>
            <WorkspaceDetailsFields
                idPrefix="onboarding"
                logo={logo}
                onLogoFile={logoCrop.pickLogo}
                name={workspaceName}
                onNameChange={(value) => {
                    setWorkspaceName(value);
                    clearErrors();
                }}
                nameError={fieldErrors.workspaceName || ''}
                nameHelperText={hasWorkspace ? 'Your workspace is already set up.' : ''}
                disabled={submitting || hasWorkspace}
            />
            <LogoCropModal {...logoCrop.modalProps} cropLogo={cropLogo} />
        </>
    );

    const renderChoices = (options, selected, setSelected, errorKey) => (
        <div className="onboarding-fields">
            <div className="onboarding-options" role="group" aria-labelledby={titleId}>
                {options.map((option) => (
                    <ChoiceTile
                        key={option}
                        label={option}
                        selectionMode="multiple"
                        selected={selected.includes(option)}
                        disabled={submitting}
                        onClick={() => {
                            setSelected((prev) => toggleValue(prev, option));
                            clearErrors();
                        }}
                    />
                ))}
            </div>
            {fieldErrors[errorKey] ? (
                <Text as="p" variant="detail-regular" className="onboarding-error" role="alert">
                    {fieldErrors[errorKey]}
                </Text>
            ) : null}
        </div>
    );

    const renderTerms = () => (
        <div className="onboarding-terms">
            <div className="onboarding-terms-box">
                <div
                    ref={termsScrollRef}
                    className="onboarding-terms-scroll"
                    tabIndex={0}
                    role="region"
                    aria-label="Terms and conditions"
                >
                    <Text as="p" variant="base-regular" color="primary" className="onboarding-terms-text">
                        {ONBOARDING_TERMS_TEXT}
                    </Text>
                </div>
                <ScrollBar containerRef={termsScrollRef} />
            </div>
            <div className="onboarding-terms-consent">
                {CONSENT_ITEMS.map((item) => (
                    <ConsentCheckbox
                        key={item.id}
                        item={item}
                        checked={Boolean(consents[item.id])}
                        disabled={submitting || agreed}
                        onToggle={() => toggleConsent(item.id)}
                    />
                ))}
            </div>
        </div>
    );

    const renderBody = () => {
        switch (current.id) {
            case 'about':
                return renderAbout();
            case 'workspace':
                return renderWorkspace();
            case 'improve':
                return renderChoices(IMPROVE_GOAL_OPTIONS, goals, setGoals, 'improve_goals');
            case 'audience':
                return renderChoices(MODEL_AUDIENCE_OPTIONS, audience, setAudience, 'model_audience');
            default:
                return renderTerms();
        }
    };

    const footer =
        current.id === 'terms' ? (
            <Button
                size="large"
                variant="primary"
                disabled={!allConsented || submitting}
                aria-busy={submitting || undefined}
                onClick={finish}
            >
                Continue to Specific
            </Button>
        ) : (
            <Button
                size="large"
                variant="primary"
                disabled={submitting}
                aria-busy={submitting || undefined}
                onClick={nextHandlers[current.id]}
            >
                Next
            </Button>
        );

    return (
        <OnboardingLayout
            variant={current.variant || 'split'}
            artwork={current.artwork}
            footer={footer}
            progress={
                current.id === 'terms' ? null : (
                    <OnboardingProgress segments={progressSegments} position={progressPosition} />
                )
            }
        >
            <div className="onboarding-header">
                {step > 0 ? (
                    <Button
                        size="tiny"
                        variant="ghost"
                        aria-label="Back"
                        tooltip="Back"
                        disabled={submitting}
                        startIcon={<SparkIcon slug="arrow-left" size="medium" />}
                        onClick={goBack}
                    />
                ) : null}
                <Text as="h1" id={titleId} variant="heading-4-semibold" color="primary">
                    {current.title}
                </Text>
                {current.subtitle ? (
                    <Text as="p" variant="lead-regular" color="primary">
                        {current.subtitle}
                    </Text>
                ) : null}
            </div>
            {renderBody()}
            {error ? (
                <Text as="p" variant="base-regular" role="alert" className="onboarding-error">
                    {error}
                </Text>
            ) : null}
        </OnboardingLayout>
    );
}
